import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { getOrgChart } from "../services/orgChartApi";
import { getKpiMe, getKpiOverview } from "../services/kpiApi";
import { getTablePrefs, saveTablePrefs } from "../services/uiPrefsApi";
import { currentPeriod, deltaClass, money, periodLabel, shiftPeriod } from "../utils/kpiFormat";
import {
    COMPANY_BLOCKS, DEFAULT_COMPANY_PREFS, childrenMap, descendantIds, moveBlock, normalizeCompanyPrefs,
    roleLabel, rootUnits, unitPath, unitPeople,
} from "../utils/companyTree";

/**
 * «Компания» — одна история: структура (№9, Чат 2) + люди + KPI (№10) по подразделениям. [Claude] 2026-11-03, Чат 6, №35.
 * Как в Битрикс24 (схема с руководителями и заместителями) — кнопкой «Схема» и в «Структуре компании»;
 * здесь — карточка подразделения из настраиваемых блоков. Блоки, порядок и вид — личные настройки (ui-preferences company_hub).
 * KPI показывается только тем, кому он открыт в «KPI → Настройки → Доступ»; таблицы КПЭ привязываются к подразделениям там же.
 */

const PREFS_KEY = "company_hub";
const input = "min-h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-amber-400";
const btn = "min-h-9 rounded-lg px-3 py-1.5 text-sm font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;

function TreeRow({ unit, depth, kids, selected, onSelect, collapsed, toggle, counts }) {
    const has = (kids.get(unit.id) || []).length > 0;
    const open = !collapsed.has(unit.id);
    return (
        <>
            <div className={`flex items-center gap-1 rounded-md pr-2 text-sm ${selected === unit.id ? "bg-amber-100 text-slate-900" : "hover:bg-slate-50 text-slate-700"}`}
                style={{ paddingLeft: 4 + depth * 14 }}>
                <button type="button" className="w-5 text-slate-400" onClick={() => has && toggle(unit.id)} aria-label={open ? "Свернуть" : "Развернуть"}>
                    {has ? (open ? "▾" : "▸") : ""}
                </button>
                <button type="button" className="flex-1 truncate py-1.5 text-left" onClick={() => onSelect(unit.id)}>{unit.name}</button>
                <span className="text-xs text-slate-400">{counts.get(unit.id) || ""}</span>
            </div>
            {has && open && (kids.get(unit.id) || []).map((c) => (
                <TreeRow key={c.id} unit={c} depth={depth + 1} kids={kids} selected={selected} onSelect={onSelect}
                    collapsed={collapsed} toggle={toggle} counts={counts} />
            ))}
        </>
    );
}

function ChartNode({ unit, kids, heads, selected, onSelect }) {
    const children = kids.get(unit.id) || [];
    return (
        <div className="flex flex-col items-center">
            <button type="button" onClick={() => onSelect(unit.id)}
                className={`min-w-40 max-w-56 rounded-lg border px-3 py-2 text-left shadow-sm ${selected === unit.id ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                <p className="text-sm font-semibold text-slate-900">{unit.name}</p>
                {(heads.get(unit.id) || []).slice(0, 2).map((h) => (
                    <p key={h.id} className="truncate text-xs text-slate-500">{h.full_name}</p>
                ))}
            </button>
            {children.length > 0 && (
                <>
                    <div className="h-4 w-px bg-slate-300" />
                    <div className="flex gap-4 border-t border-slate-300 pt-4">
                        {children.map((c) => <ChartNode key={c.id} unit={c} kids={kids} heads={heads} selected={selected} onSelect={onSelect} />)}
                    </div>
                </>
            )}
        </div>
    );
}

function Block({ title, children, extra }) {
    return (
        <section className="rounded-xl border border-slate-200 bg-white">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
                <h2 className="font-semibold text-slate-900">{title}</h2>
                <div className="flex-1" />
                {extra}
            </header>
            <div className="p-4">{children}</div>
        </section>
    );
}

function ViewSettings({ prefs, onChange }) {
    const hidden = new Set(prefs.hidden);
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
            <p className="mb-2 text-xs text-slate-500">Какие блоки показывать в карточке подразделения и в каком порядке (только у вас).</p>
            <ul className="space-y-1">
                {prefs.blocks.map((key, i) => {
                    const b = COMPANY_BLOCKS.find((x) => x.key === key);
                    return (
                        <li key={key} className="flex items-center gap-2">
                            <input type="checkbox" checked={!hidden.has(key)} id={`blk-${key}`}
                                onChange={(e) => onChange({ hidden: e.target.checked ? prefs.hidden.filter((k) => k !== key) : [...prefs.hidden, key] })} />
                            <label htmlFor={`blk-${key}`} className="flex-1">{b?.label || key}</label>
                            <button type="button" className="px-1 text-slate-500 disabled:opacity-30" disabled={i === 0}
                                onClick={() => onChange({ blocks: moveBlock(prefs.blocks, key, -1) })} aria-label="Выше">↑</button>
                            <button type="button" className="px-1 text-slate-500 disabled:opacity-30" disabled={i === prefs.blocks.length - 1}
                                onClick={() => onChange({ blocks: moveBlock(prefs.blocks, key, 1) })} aria-label="Ниже">↓</button>
                        </li>
                    );
                })}
            </ul>
            <label className="mt-3 flex items-center gap-2">
                <input type="checkbox" checked={prefs.include_children} onChange={(e) => onChange({ include_children: e.target.checked })} />
                Люди и KPI — вместе с дочерними подразделениями
            </label>
        </div>
    );
}

export default function CompanyPage() {
    const { showAlert } = useAlertContext();
    const [params, setParams] = useSearchParams();
    const [chart, setChart] = useState(null);
    const [error, setError] = useState("");
    const [prefs, setPrefs] = useState(DEFAULT_COMPANY_PREFS);
    const [showSettings, setShowSettings] = useState(false);
    const [collapsed, setCollapsed] = useState(new Set());
    const [search, setSearch] = useState("");
    const [kpiMe, setKpiMe] = useState(null);
    const [kpi, setKpi] = useState(null);
    const [period, setPeriod] = useState(currentPeriod());
    const saveTimer = useRef(null);

    useEffect(() => {
        let alive = true;
        getOrgChart().then((d) => { if (alive) setChart(d); }).catch((e) => { if (alive) setError(e?.message || "Не удалось загрузить структуру"); });
        Promise.resolve(getTablePrefs(PREFS_KEY)).then((p) => { if (alive) setPrefs(normalizeCompanyPrefs(p)); }).catch(() => {});
        getKpiMe().then((m) => { if (alive) setKpiMe(m); }).catch(() => { if (alive) setKpiMe({ level: 0 }); });
        return () => { alive = false; };
    }, []);

    const updatePrefs = useCallback((patch) => {
        setPrefs((prev) => {
            const next = normalizeCompanyPrefs({ ...prev, ...patch });
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => { Promise.resolve(saveTablePrefs(PREFS_KEY, next)).catch(() => {}); }, 400);
            return next;
        });
    }, []);

    const units = useMemo(() => chart?.units || [], [chart]);
    const kids = useMemo(() => childrenMap(units), [units]);
    const roots = useMemo(() => rootUnits(units), [units]);
    const byId = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
    const empById = useMemo(() => new Map((chart?.employees || []).map((e) => [e.id, e])), [chart]);

    const paramUnit = Number(params.get("unit")) || null;
    const selectedId = (paramUnit && byId.has(paramUnit) && paramUnit) || (prefs.last_unit && byId.has(prefs.last_unit) && prefs.last_unit) || null;
    const select = (id) => { setParams(id ? { unit: String(id) } : {}); updatePrefs({ last_unit: id }); };

    const heads = useMemo(() => {
        const map = new Map();
        (chart?.members || []).filter((m) => m.role === "head" || m.role === "deputy").forEach((m) => {
            const e = empById.get(m.employee_id);
            if (!e) return;
            if (!map.has(m.unit_id)) map.set(m.unit_id, []);
            map.get(m.unit_id).push({ ...e, role: m.role });
        });
        map.forEach((l) => l.sort((a, b) => (a.role === "head" ? 0 : 1) - (b.role === "head" ? 0 : 1)));
        return map;
    }, [chart, empById]);

    const counts = useMemo(() => {
        const map = new Map();
        units.forEach((u) => map.set(u.id, unitPeople(descendantIds(u.id, units), chart?.members, chart?.employees, units).length));
        return map;
    }, [units, chart]);

    const scope = useMemo(() => {
        if (!selectedId) return new Set(units.map((u) => u.id));
        return prefs.include_children ? descendantIds(selectedId, units) : new Set([selectedId]);
    }, [selectedId, units, prefs.include_children]);

    const people = useMemo(() => unitPeople(scope, chart?.members, chart?.employees, units), [scope, chart, units]);

    const kpiAllowed = (kpiMe?.level || 0) > 0;
    useEffect(() => {
        if (!kpiAllowed) return;
        let alive = true;
        getKpiOverview(period, selectedId || undefined)
            .then((d) => { if (alive) setKpi(d); })
            .catch((e) => { if (alive) { setKpi(null); if (e?.status !== 403) showAlert?.(e?.message || "Не удалось загрузить KPI", "error"); } });
        return () => { alive = false; };
    }, [kpiAllowed, period, selectedId, showAlert]);

    const kpiRows = useMemo(() => {
        const rows = kpi?.groups || [];
        if (!selectedId) return rows;
        return rows.filter((r) => (r.group?.unit_ids || []).some((id) => scope.has(id)));
    }, [kpi, selectedId, scope]);
    const kpiTotal = useMemo(() => kpiRows.reduce((acc, r) => ({
        kpi_plan: acc.kpi_plan + (Number(r.summary?.kpi_plan) || 0),
        kpi_calc: acc.kpi_calc + (Number(r.summary?.kpi_calc) || 0),
        kpi_delta: acc.kpi_delta + (Number(r.summary?.kpi_delta) || 0),
        total_pay: acc.total_pay + (Number(r.summary?.total_pay) || 0),
        cards: acc.cards + (Number(r.summary?.cards) || 0),
    }), { kpi_plan: 0, kpi_calc: 0, kpi_delta: 0, total_pay: 0, cards: 0 }), [kpiRows]);

    const filteredUnits = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return null;
        const hitUnits = units.filter((u) => String(u.name).toLowerCase().includes(q));
        const hitPeople = (chart?.employees || []).filter((e) => String(e.full_name).toLowerCase().includes(q)).slice(0, 20);
        return { hitUnits, hitPeople };
    }, [search, units, chart]);

    const unitOfEmployee = (empId) => (chart?.members || []).find((m) => m.employee_id === empId)?.unit_id;

    const selected = selectedId ? byId.get(selectedId) : null;
    const path = selectedId ? unitPath(selectedId, units) : [];
    const subunits = selectedId ? (kids.get(selectedId) || []) : roots;
    const roles = chart?.roles;

    const blocks = {
        summary: (
            <Block key="summary" title="Сводка">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[["Сотрудников", people.length], ["Подразделений", selectedId ? scope.size - 1 : units.length],
                        ["Руководителей", people.filter((p) => p.member.role === "head").length],
                        ["Заместителей", people.filter((p) => p.member.role === "deputy").length]].map(([l, v]) => (
                        <div key={l} className="rounded-lg border border-slate-200 px-3 py-2">
                            <p className="text-xs text-slate-500">{l}</p>
                            <p className="text-lg font-bold text-slate-800">{v}</p>
                        </div>
                    ))}
                </div>
                {selected && (heads.get(selected.id) || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                        {(heads.get(selected.id) || []).map((h) => (
                            <div key={`${h.id}-${h.role}`} className="rounded-lg bg-slate-50 px-3 py-2">
                                <p className="text-xs text-slate-500">{roleLabel(roles, h.role)}</p>
                                <p className="text-sm font-semibold text-slate-900">{h.full_name}</p>
                                {h.position && <p className="text-xs text-slate-500">{h.position}</p>}
                            </div>
                        ))}
                    </div>
                )}
                {selected?.description && <p className="mt-3 whitespace-pre-line text-sm text-slate-600">{selected.description}</p>}
            </Block>
        ),
        people: (
            <Block key="people" title={`Сотрудники · ${people.length}`}>
                {!people.length && <p className="text-sm text-slate-500">В подразделении пока нет людей — добавляются в «Структуре компании».</p>}
                <ul className="divide-y divide-slate-100">
                    {people.slice(0, 300).map(({ member, employee, unit }) => (
                        <li key={employee.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                            <Link to={`/employees/${employee.id}`} className="font-medium text-slate-800 hover:underline">{employee.full_name}</Link>
                            <span className="text-slate-500">{member.title || employee.position || ""}</span>
                            {member.role !== "member" && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">{roleLabel(roles, member.role)}</span>}
                            <div className="flex-1" />
                            {unit && unit.id !== selectedId && (
                                <button type="button" className="text-xs text-slate-400 hover:text-slate-700" onClick={() => select(unit.id)}>{unit.name}</button>
                            )}
                        </li>
                    ))}
                </ul>
            </Block>
        ),
        kpi: kpiAllowed ? (
            <Block key="kpi" title="KPI"
                extra={(
                    <div className="flex items-center gap-1">
                        <button type="button" className={btnGhost} onClick={() => setPeriod(shiftPeriod(period, -1))} aria-label="Предыдущий месяц">←</button>
                        <span className="px-1 text-sm text-slate-700">{periodLabel(period)}</span>
                        <button type="button" className={btnGhost} onClick={() => setPeriod(shiftPeriod(period, 1))} aria-label="Следующий месяц">→</button>
                        {kpiMe?.is_admin && <Link to="/kpi/settings" className="ml-2 text-xs text-slate-500 underline">привязка</Link>}
                    </div>
                )}>
                {!kpiRows.length ? (
                    <p className="text-sm text-slate-500">
                        {selectedId ? "К этому подразделению не привязаны таблицы КПЭ за этот месяц." : "За этот месяц карт КПЭ нет."}
                        {kpiMe?.is_admin && selectedId && <> Привязать — в <Link className="underline" to="/kpi/settings">KPI → Настройки</Link>.</>}
                    </p>
                ) : (
                    <>
                        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                            {[["Карт", kpiTotal.cards, ""], ["План", money(kpiTotal.kpi_plan), ""], ["Расчёт", money(kpiTotal.kpi_calc), ""],
                                ["Дельта", money(kpiTotal.kpi_delta), deltaClass(kpiTotal.kpi_delta)], ["К оплате", money(kpiTotal.total_pay), "text-slate-900"]].map(([l, v, cls]) => (
                                <div key={l} className="rounded-lg border border-slate-200 px-3 py-2">
                                    <p className="text-xs text-slate-500">{l}</p>
                                    <p className={`text-lg font-bold ${cls || "text-slate-800"}`}>{v}</p>
                                </div>
                            ))}
                        </div>
                        <ul className="divide-y divide-slate-100">
                            {kpiRows.map((r) => (
                                <li key={r.month.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                                    <Link to={`/kpi/months/${r.month.id}`} className="font-medium text-slate-800 hover:underline">{r.group?.name}</Link>
                                    <span className="text-slate-500">карт {r.summary?.cards ?? r.cards?.length ?? 0}</span>
                                    <span className={deltaClass(r.summary?.kpi_delta)}>дельта {money(r.summary?.kpi_delta)}</span>
                                    <span className="font-semibold text-slate-800">к оплате {money(r.summary?.total_pay)}</span>
                                    {r.month.status === "closed" && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">закрыт</span>}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </Block>
        ) : null,
        units: (
            <Block key="units" title={selectedId ? `Подразделения внутри · ${subunits.length}` : "Подразделения верхнего уровня"}>
                {!subunits.length && <p className="text-sm text-slate-500">Дочерних подразделений нет.</p>}
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {subunits.map((u) => (
                        <button key={u.id} type="button" onClick={() => select(u.id)}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-left hover:border-slate-400">
                            <p className="text-sm font-semibold text-slate-900">{u.name}</p>
                            <p className="text-xs text-slate-500">
                                {(heads.get(u.id) || []).find((h) => h.role === "head")?.full_name || "руководитель не назначен"} · {counts.get(u.id) || 0} чел.
                            </p>
                        </button>
                    ))}
                </div>
            </Block>
        ),
    };

    const hidden = new Set(prefs.hidden);

    return (
        <MainLayout>
            <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="mr-2 text-xl font-bold text-slate-900">Компания</h1>
                    <div className="flex overflow-hidden rounded-lg border border-slate-200 text-sm">
                        {[["tree", "Карточки"], ["chart", "Схема"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => updatePrefs({ view: k })}
                                className={`px-3 py-1.5 ${prefs.view === k ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>{l}</button>
                        ))}
                    </div>
                    <div className="flex-1" />
                    <Link to="/organization" className={btnGhost}>Редактировать структуру</Link>
                    {kpiMe?.is_admin && <Link to="/kpi/settings" className={btnGhost}>Настройки KPI</Link>}
                    <button type="button" className={btnGhost} onClick={() => setShowSettings((v) => !v)}>
                        {showSettings ? "Скрыть настройки вида" : "Настроить вид"}
                    </button>
                </div>

                {showSettings && <ViewSettings prefs={prefs} onChange={updatePrefs} />}
                {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
                {!chart && !error && <p className="text-sm text-slate-500">Загрузка…</p>}
                {chart && !units.length && (
                    <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
                        Структура пока пустая. Создайте подразделения в <Link className="underline" to="/organization">«Структуре компании»</Link>.
                    </p>
                )}

                {chart && units.length > 0 && prefs.view === "chart" && (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex min-w-max justify-center gap-6">
                            {roots.map((r) => <ChartNode key={r.id} unit={r} kids={kids} heads={heads} selected={selectedId} onSelect={select} />)}
                        </div>
                    </div>
                )}

                {chart && units.length > 0 && (
                    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                        <aside className="space-y-2">
                            <input className={`${input} w-full`} placeholder="Поиск: подразделение или сотрудник" value={search}
                                onChange={(e) => setSearch(e.target.value)} />
                            <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1">
                                <button type="button" onClick={() => select(null)}
                                    className={`w-full rounded-md px-2 py-1.5 text-left text-sm font-semibold ${!selectedId ? "bg-amber-100" : "hover:bg-slate-50"}`}>
                                    Вся компания
                                </button>
                                {filteredUnits ? (
                                    <>
                                        {filteredUnits.hitUnits.map((u) => (
                                            <button key={`u${u.id}`} type="button" onClick={() => select(u.id)}
                                                className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50">{u.name}</button>
                                        ))}
                                        {filteredUnits.hitPeople.map((e) => (
                                            <button key={`e${e.id}`} type="button" onClick={() => { const u = unitOfEmployee(e.id); if (u) select(u); }}
                                                className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-slate-600 hover:bg-slate-50">
                                                👤 {e.full_name}{unitOfEmployee(e.id) ? ` · ${byId.get(unitOfEmployee(e.id))?.name || ""}` : " · нет в структуре"}
                                            </button>
                                        ))}
                                        {!filteredUnits.hitUnits.length && !filteredUnits.hitPeople.length && <p className="px-2 py-1.5 text-sm text-slate-500">Ничего не найдено</p>}
                                    </>
                                ) : roots.map((r) => (
                                    <TreeRow key={r.id} unit={r} depth={0} kids={kids} selected={selectedId} onSelect={select} counts={counts}
                                        collapsed={collapsed} toggle={(id) => setCollapsed((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
                                ))}
                            </div>
                        </aside>

                        <main className="min-w-0 space-y-4">
                            <div className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
                                <button type="button" className="hover:text-slate-800" onClick={() => select(null)}>Компания</button>
                                {path.map((u) => (
                                    <span key={u.id}>
                                        {" / "}
                                        <button type="button" className={u.id === selectedId ? "font-semibold text-slate-900" : "hover:text-slate-800"} onClick={() => select(u.id)}>{u.name}</button>
                                    </span>
                                ))}
                            </div>
                            {prefs.blocks.filter((k) => !hidden.has(k)).map((k) => blocks[k]).filter(Boolean)}
                            {prefs.blocks.every((k) => hidden.has(k) || !blocks[k]) && (
                                <p className="text-sm text-slate-500">Все блоки скрыты — включите их в «Настроить вид».</p>
                            )}
                        </main>
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
